import { createApi } from '@reduxjs/toolkit/query/react';
import { createAuthBaseQuery } from './baseQuery';
import { API_BASE_URL } from '../constants/api';

// Referring-doctor CRUD for the Medical Portal's "My Doctors" page.
// All four endpoints are JWT-auth-gated and centre-scoped — the
// backend filters by `req.user.client_id`, so the slice never has to
// pass a centre id.

export const refDoctorsApi = createApi({
  reducerPath: 'refDoctorsApi',
  baseQuery: createAuthBaseQuery(API_BASE_URL),
  tagTypes: ['RefDoctor'],
  endpoints: (builder) => ({
    // GET /api/portal/ref-doctors?page=&limit=&q=&sort=&specialization=&force=
    //
    // The backend triggers an IT Dose sync transparently when the
    // centre's cache is stale (>5 min) or when force=1 is passed.
    // Pagination / search / sort / specialization filter are all
    // server-side Mongo queries — fast even with a large list.
    listRefDoctors: builder.query({
      query: ({
        page = 1, limit = 20, q = '', sort = 'name',
        specialization = '', force = false,
      } = {}) => {
        const params = new URLSearchParams();
        params.set('page',  String(page));
        params.set('limit', String(limit));
        if (q)              params.set('q', q);
        if (sort)           params.set('sort', sort);
        if (specialization) params.set('specialization', specialization);
        if (force)          params.set('force', '1');
        return `/portal/ref-doctors?${params.toString()}`;
      },
      providesTags: (result) =>
        result?.items?.length
          ? [
              ...result.items.map((d) => ({ type: 'RefDoctor', id: d._id })),
              { type: 'RefDoctor', id: 'LIST' },
            ]
          : [{ type: 'RefDoctor', id: 'LIST' }],
    }),

    // GET /api/portal/ref-doctors/specializations — distinct list of
    // specializations for the centre, powering the dynamic dropdown
    // filter in the portal. Tagged on LIST so it auto-refetches when
    // a sync brings in new specs.
    listRefDoctorSpecializations: builder.query({
      query: () => '/portal/ref-doctors/specializations',
      providesTags: [{ type: 'RefDoctor', id: 'LIST' }],
    }),

    // POST /api/portal/ref-doctors
    createRefDoctor: builder.mutation({
      query: (body) => ({
        url: '/portal/ref-doctors',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'RefDoctor', id: 'LIST' }],
    }),

    // PATCH /api/portal/ref-doctors/:id
    updateRefDoctor: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/portal/ref-doctors/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'RefDoctor', id: arg.id },
        { type: 'RefDoctor', id: 'LIST' },
      ],
    }),

    // DELETE /api/portal/ref-doctors/:id
    deleteRefDoctor: builder.mutation({
      query: (id) => ({
        url: `/portal/ref-doctors/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, id) => [
        { type: 'RefDoctor', id },
        { type: 'RefDoctor', id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useListRefDoctorsQuery,
  useListRefDoctorSpecializationsQuery,
  useCreateRefDoctorMutation,
  useUpdateRefDoctorMutation,
  useDeleteRefDoctorMutation,
} = refDoctorsApi;
